import React, { useId, useState } from 'react';
import '../styles/kairos-showcase.css';

const phases = [
  { label: 'Understand', title: 'Find the signal.', description: 'See what a role really asks of you.' },
  { label: 'Position', title: 'Make it relevant.', description: 'Connect your experience to the opportunity.' },
  { label: 'Prepare', title: 'Own your story.', description: 'Turn your experience into a clear story.' },
];

function WorkflowContent({ step }: { step: number }) {
  if (step === 1) {
    return (
      <div className="kairos-workflow__evidence">
        <span className="kairos-workflow__label">Relevant experience</span>
        <p>Led a launch across product, design, and operations.</p>
        <span className="kairos-workflow__evidence-rule" aria-hidden="true" />
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="kairos-workflow__interview">
        <span className="kairos-workflow__label">A question to prepare for</span>
        <p className="kairos-workflow__question">“How did you move a cross-team project forward?”</p>
        <div className="kairos-workflow__story" aria-label="Structure your story around context, action, and outcome">
          <span><i aria-hidden="true">01</i>Context</span>
          <span><i aria-hidden="true">02</i>Action</span>
          <span><i aria-hidden="true">03</i>Outcome</span>
        </div>
      </div>
    );
  }

  return (
    <div className="kairos-workflow__requirements">
      <div className="kairos-workflow__requirements-heading">
        <span className="kairos-workflow__label">Example role</span>
        <span>Product operations</span>
      </div>
      <div className="kairos-workflow__requirement">
        <span>Clear communication</span>
        <span className="kairos-workflow__signal">Align</span>
      </div>
      <div className="kairos-workflow__requirement">
        <span>Measurable delivery</span>
        <span className="kairos-workflow__signal">Deliver</span>
      </div>
    </div>
  );
}

interface KairosShowcaseProps {
  paused?: boolean;
}

export const KairosShowcase: React.FC<KairosShowcaseProps> = ({ paused = false }) => {
  const [step, setStep] = useState(0);
  const panelId = useId();
  const phase = phases[step];
  const workflowStyle = {
    '--workflow-step': step,
    '--workflow-progress': (step + 1) / phases.length,
  } as React.CSSProperties;

  return (
    <div className={`kairos-workflow${paused ? ' is-paused' : ''}`} data-step={step} style={workflowStyle}>
      <div className="kairos-workflow__topline">
        <span className="kairos-workflow__label"><span className="kairos-workflow__status" aria-hidden="true" />Example workflow</span>
        <span className="kairos-workflow__page" aria-hidden="true">0{step + 1}<span> / 03</span></span>
      </div>

      <div id={panelId} className="kairos-workflow__panel" aria-live="polite" aria-atomic="true">
        <div key={`intro-${step}`} className="kairos-workflow__intro">
          <h3>{phase.title}</h3>
          <p>{phase.description}</p>
        </div>
        <div className="kairos-workflow__journey">
          <span className="kairos-workflow__thread" aria-hidden="true"><span /></span>
          <div className="kairos-workflow__anchor">
            <span className="kairos-workflow__marker" aria-hidden="true" />
            <div>
              <span className="kairos-workflow__label">Role requirement</span>
              <p>Cross-team ownership</p>
            </div>
            <span className="kairos-workflow__signal">Lead</span>
          </div>
          <div key={`output-${step}`} className="kairos-workflow__output">
            <WorkflowContent step={step} />
          </div>
        </div>
      </div>

      <div className="kairos-workflow__steps" role="group" aria-label="Explore the Kairos workflow">
        <span className="kairos-workflow__rail" aria-hidden="true" />
        {phases.map((item, index) => (
          <button
            key={item.label}
            type="button"
            className="kairos-workflow__step"
            aria-pressed={step === index}
            aria-controls={panelId}
            onClick={() => setStep(index)}
          >
            <span className="kairos-workflow__step-number">0{index + 1}</span>
            <span>{item.label}</span>
            <svg aria-hidden="true" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M8 3l5 5-5 5" /></svg>
          </button>
        ))}
      </div>
    </div>
  );
};

export default KairosShowcase;
